import {LitElement, html, css} from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { SldsBaseElement } from '../components/slds-base-element.js';

@customElement('slds-lookup')
export class SldsLookup extends SldsBaseElement {
  static get properties() {
    return {
      label: { type: String },
      placeholder: { type: String },
      currentPlaceholder: { type: String },
      items: { type: Array },
      selectedItems: { type: Array },
      searchResults: { type: Array },
      isOpen: { type: Boolean },
      searchInput: { type: String },
      isCombobox: { type: Boolean },
      url: { type: String },
    };
  }

  constructor() {
    super();
    this.label = '';
    this.placeholder = '';
    this.currentPlaceholder = '';
    this.items = [];
    this.selectedItems = [];
    this.searchResults = [];
    this.isOpen = false;
    this.searchInput = '';
    this.isCombobox = false;
    this.url = '';
    document.addEventListener('click', (function(e) {
      if (this.isOpen) {
        const path = e.composedPath();
        let insideMenuClick = false;
        if(!(path === undefined || path.length == 0)) {
         insideMenuClick = path.find((el) => { return el == this; });
        }
        if(insideMenuClick) {
          //inside element
        } else {
          //outside element
          this.isOpen = false;
          const event = new CustomEvent('blur', { open: this.isOpen, bubbles: true });
          this.dispatchEvent(event);
        }
      }
    }).bind(this), false);

    // on press enter, take the current value and add it to the selected items (only if focus is on the input)
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && this.shadowRoot.activeElement === this.shadowRoot.querySelector('input')) {
        // Only add if the value is not already in the selected items
        const slug = this.searchInput.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        if (!this.selectedItems.some(item => item.slug === slug)) {
          this.handleSelect({
            tag: this.searchInput, 
            slug: slug
          });
        }
      }
    });
  }

  openDropdown() {
    if (this.searchResults.length > 0) {  
      this.isOpen = true;
    }
  }

  closeDropdown() {
    this.isOpen = false;
  }

  handleFocus(e) {
    this.openDropdown();
    this.handleSearch(e);
  }

  handleSearch(e) {
    this.searchInput = e.target.value;

    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    this.debounceTimeout = setTimeout(async () => {
      if (this.searchInput.length >= 3 || true) {
        try {
          let response;
          if (this.searchInput.length >= 3) {
            response = await fetch(`${this.url}?q=${encodeURIComponent(this.searchInput)}`);
          }
          else {
            response = await fetch(`${this.url}`);
          }
          if (response.ok) {
            this.searchResults = await response.json();
            // remove items that are already selected
            this.searchResults = this.searchResults.filter(result => !this.selectedItems.some(selectedItem => selectedItem.slug === result.slug));
            if (this.searchResults.length > 0) {
              this.openDropdown();
            } else {
              this.closeDropdown();
            }
          } else {
            console.error('Error fetching data:', response.status, response.statusText);
          }
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      } else {
        this.searchResults = [];
        this.closeDropdown();
      }
    }, 300);
  }

  handleSelect(item) {
    this.selectedItems.push(item);
    this.searchResults = this.searchResults.filter(result => result.slug !== item.slug);
    if (this.selectedItems.length > 0) {
      this.currentPlaceholder = this.selectedItems.length + " Options Selected";
    } else {
      this.currentPlaceholder = this.placeholder;
    }
    this.dispatchEvent(new CustomEvent('selected', { detail: { selectedItems: this.selectedItems } }));
    this.closeDropdown();
  }

  removeItem(item) {
    this.selectedItems = this.selectedItems.filter(selectedItem => selectedItem.slug !== item.slug);
    this.searchResults.push(item);
    if (this.selectedItems.length > 0) {
      this.currentPlaceholder = this.selectedItems.length + " Options Selected";
    } else {
      this.currentPlaceholder = this.placeholder;
    }
    this.dispatchEvent(new CustomEvent('selected', { detail: { selectedItems: this.selectedItems } }));
    this.closeDropdown();
  }

  clearSelection() {
    this.searchInput = '';
    if (this.selectedItems.length > 0) {
      this.currentPlaceholder = this.selectedItems.length + " Options Selected";
    } else {
      this.currentPlaceholder = this.placeholder;
    }
    this.dispatchEvent(new CustomEvent('selected', { detail: { selectedItems: this.selectedItems } }));
    this.closeDropdown();
  }

  static styles = [
    super.styles,
    css`
      .slds-dropdown {
        background: var(--background-color, #fff);
        color: var(--text-color, #181818);
        border: 1px solid var(--border-color, #e5e5e5);
      }
    .slds-dropdown__item>a {
      color: var(--text-color, #181818);
    }
    .slds-listbox_vertical .slds-listbox__option:focus, .slds-listbox_vertical .slds-listbox__option:hover {
      background-color: var(--table-hover, #f3f3f3);
    }
    .slds-dropdown__item span {
      color: var(--text-color, #181818);
    }
    .slds-pill {
      background: var(--background-color, #fff);
      border-color: var(--border-color, #e5e5e5);
    }
    .slds-listbox_selection-group {
      display: initial;
      }
    `
  ];

  render() {
    return html`
      <div class="slds-form-element">
        <label class="slds-form-element__label" for="slds-lookup">${this.label}</label>
        <div class="slds-form-element__control">
          <div class=${classMap({ 'slds-combobox_container': this.isCombobox, 'slds-lookup': !this.isCombobox })}>
            <div
              class="slds-combobox slds-dropdown-trigger slds-dropdown-trigger_click ${this.isOpen ? 'slds-is-open' : ''}"
              aria-expanded="${this.isOpen}"
              aria-haspopup="listbox"
              role="combobox"
            >
              <div class="slds-combobox__form-element slds-input-has-icon slds-input-has-icon_right" role="none">
                <input
                  type="text"
                  class="slds-input slds-combobox__input"
                  id="slds-lookup"
                  aria-autocomplete="list"
                  aria-controls="slds-lookup-listbox"
                  autocomplete="off"
                  role="textbox"
                  placeholder="${(this.currentPlaceholder.length === 0 ? this.placeholder : this.currentPlaceholder)}"
                  .value="${this.searchInput}"
                  @input="${this.handleSearch}"
                  @focus="${this.handleFocus}"
                />
                ${this.searchResults.length > 0
                  ? html`<button
                      class="slds-button slds-button_icon slds-input__icon slds-input__icon_right"
                      title="Remove selected option"
                      @click="${this.clearSelection}"
                    >
                      <svg class="slds-button__icon" aria-hidden="true">
                        <use xlink:href="/assets/icons/utility-sprite/svg/symbols.svg#close"></use>
                      </svg>
                      <span class="slds-assistive-text">Remove selected option</span>
                    </button>`
                  : ''}
              </div>
              <ul
                class="slds-listbox slds-listbox_vertical slds-dropdown slds-dropdown_fluid slds-m-bottom_medium"
                id="slds-lookup-listbox"
                role="listbox"
                aria-label="${this.label}"
              >
                ${this.searchResults.map(
                  item => html`
                    <li role="presentation" class="slds-listbox__item" @click="${() => this.handleSelect(item)}">
                      <div class="slds-media slds-listbox__option slds-listbox__option_plain slds-media_small" role="option">
                        <span class="slds-media__figure slds-listbox__option-icon"></span>
                        <span class="slds-media__body">
                          <span class="slds-truncate" title="${item.tag}">${item.tag}</span>
                        </span>
                      </div>
                    </li>
                  `
                )}
              </ul>
            </div>
          </div>
          ${this.selectedItems.length > 0
            ? html`
          <div class="slds-listbox_selection-group">
            <ul class="slds-listbox slds-listbox_horizontal" aria-label="Selected Options:">
              ${this.selectedItems.map(
                item => html`
                  <li class="slds-listbox-item">
                    <span class="slds-pill">
                      <span class="slds-pill__label slds-m-left_xx-small" title="${item.tag}">${item.tag}</span>
                      <span class="slds-icon_container slds-pill__remove" title="Remove">
                        <button @click="${() => this.removeItem(item)}" class="slds-button slds-button_icon slds-button_icon slds-pill__remove" title="Remove">
                          <svg class="slds-button__icon" aria-hidden="true">
                            <use xlink:href="/assets/icons/utility-sprite/svg/symbols.svg#close"></use>
                          </svg>
                          <span class="slds-assistive-text">Remove</span>
                        </button>
                      </span>
                    </span>
                  </li>
                `
              )}
            </ul>
          </div>
          `
          : ''}
        </div>
      </div>
    `;
  }
}